import React, { useState } from 'react';
import { classes } from './useStyles';
import {
  Box,
  Popover,
  IconButton,
  FormControl,
  FormGroup,
  FormControlLabel,
  Switch,
  Stack,
  Tooltip
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { IDropdownProps } from '../types';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import SortableField from './SortableField';

export default function FieldDropdown({
  sortableColumns,
  setSortableColumns,
  density,
  setDensity,
  postDensitySetting,
  archived,
  jobStatusFeature,
  userPermissions
}: IDropdownProps) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleFieldSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSortableColumns = sortableColumns.map((field) => {
      if (field.id === event.target.name) {
        return { ...field, enabled: event.target.checked };
      } else {
        return field;
      }
    });
    setSortableColumns(newSortableColumns);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = sortableColumns.map((c) => c.id).indexOf(active.id);
      const newIndex = sortableColumns.map((c) => c.id).indexOf(over.id);
      const newArray = arrayMove(sortableColumns, oldIndex, newIndex);

      setSortableColumns(newArray);
    }
  }

  const open = Boolean(anchorEl);

  return (
    <Tooltip title="Table settings" placement="top" arrow>
      <Box>
        <IconButton
          id={`field-dropdown-${archived ? 'archived' : 'current'}-button}`}
          sx={{
            ...classes.iconButton,
            backgroundColor: open
              ? classes.iconButtonOpen.backgroundColor
              : classes.iconButton.backgroundColor
          }}
          onClick={handleClick}
        >
          <ViewColumnIcon />
        </IconButton>
        <Popover
          id="field-dropdown-popover"
          sx={classes.popover}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Box sx={classes.popoverContent}>
            <Stack sx={classes.tableSettingsHeader}>Column</Stack>
            <FormControl component="fieldset" variant="standard">
              <FormGroup id="field-dropdown-switches">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={sortableColumns} strategy={verticalListSortingStrategy}>
                    {sortableColumns.map((field, index) => {
                      if (field.canToggle === false || (!jobStatusFeature && field.id === 'status'))
                        return null;
                      else if (
                        field.label === 'RM User' &&
                        !userPermissions?.recruitment_team_enabled
                      )
                        return null;
                      else if (
                        field.label === 'Entity' &&
                        !userPermissions?.Jobs?.['Show Job Sub-Entity']
                      )
                        return null;
                      return (
                        <SortableField
                          key={index}
                          field={field}
                          handleFieldSwitchChange={handleFieldSwitchChange}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </FormGroup>
            </FormControl>
            <Stack sx={classes.tableSettingsHeader} paddingTop="16px">
              Density
            </Stack>
            <FormControl>
              <FormGroup>
                {['Default', 'Compact', 'Ultra Compact'].map((size, index) => {
                  const isSwitched = size === density;
                  return (
                    <FormControlLabel
                      sx={{
                        ...classes.switchLabels,
                        '& .MuiFormControlLabel-label': isSwitched
                          ? classes.switchLabels['& .MuiFormControlLabel-label']
                          : {
                              ...classes.switchLabels['& .MuiFormControlLabel-label'],
                              ...classes.labelActive['& .MuiFormControlLabel-label']
                            }
                      }}
                      key={index}
                      control={
                        <Switch
                          sx={isSwitched ? classes.switchActive : classes.switch}
                          checked={isSwitched}
                          onChange={(event) => {
                            setDensity(size);
                            postDensitySetting(size);
                          }}
                          name={'density'}
                        />
                      }
                      label={size}
                      labelPlacement="start"
                    />
                  );
                })}
              </FormGroup>
            </FormControl>
          </Box>
        </Popover>
      </Box>
    </Tooltip>
  );
}
